import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { Store } from '../redux/store';

/**
 * Thrown when APK validation returns false.
 */
export class AppBlockedError extends Error {
  constructor() {
    super('API call blocked: APK validation failed.');
    this.name = 'AppBlockedError';
  }
}

/**
 * Thrown when the device is detected as rooted (confidence >= 3).
 */
export class RootedDeviceError extends Error {
  constructor() {
    super('API call blocked: rooted device detected.');
    this.name = 'RootedDeviceError';
  }
}

/**
 * Polls Redux state until the value resolves from null with a 10s timeout.
 */
function waitForState<T>(
  selector: () => T | null,
  timeoutMs = 10000
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Security check timed out')), timeoutMs);

    const check = () => {
      const value = selector();
      if (value !== null) {
        clearTimeout(timeout);
        resolve(value as T);
        return;
      }
      setTimeout(check, 100);
    };

    check();
  });
}

/**
 * Drop-in replacement for fetch() with APK validation + root detection interceptor.
 *
 * Before every request:
 *   APK valid = false    → blocked, AppBlockedError thrown
 *   Device rooted (high risk) → blocked, RootedDeviceError thrown
 *   Both pass            → request proceeds normally
 */
export interface AuthFetchInit extends RequestInit {
  onUploadProgress?: (event: any) => void;
}

async function performRequest(
  input: RequestInfo | URL,
  init?: AuthFetchInit
): Promise<Response> {
  if (!init?.onUploadProgress) {
    return fetch(input, init);
  }

  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
      ? input.href
      : (input as Request).url;

  const axiosResponse = await axios({
    method: (init?.method ?? 'GET') as any,
    url,
    headers: init?.headers as Record<string, string> | undefined,
    data: init?.body,
    onUploadProgress: init.onUploadProgress,
    validateStatus: () => true,
    responseType: 'text',
    withCredentials: init?.credentials === 'include',
    signal: init?.signal,
  });

  const body =
    typeof axiosResponse.data === 'string'
      ? axiosResponse.data
      : JSON.stringify(axiosResponse.data);

  return new Response(body, {
    status: axiosResponse.status,
    statusText: axiosResponse.statusText,
    headers: new Headers(
      axiosResponse.headers as Record<string, string>
    ),
  });
}

export async function authFetch(
  input: RequestInfo | URL,
  init?: AuthFetchInit
): Promise<Response> {
  if (!Capacitor.isNativePlatform()) {
    return performRequest(input, init);
  }

  const valid = await waitForState<boolean>(() => Store.getState().appToken.valid);

  if (!valid) {
    throw new AppBlockedError();
  }

  const isHighRisk = await waitForState<boolean>(() => Store.getState().rootDetection.isHighRisk);

  if (isHighRisk) {
    throw new RootedDeviceError();
  }

  return performRequest(input, init);
}
